import { createContext, useContext, useState, useCallback } from "react";
import { DEMO_SELF_AUDIT } from "@/lib/demoData";

// Session-scoped Demo Mode state. Self-Audit Confirm/Dismiss/Edit are TEMPORARY and local-only:
// they live in React state (this provider), never touch the backend, and reset on refresh
// (provider remounts), on Exit (resetDemo), and on logout (provider unmounts).
const DemoContext = createContext(null);

// Deep-clone the immutable fixture so edits never mutate the canonical sample.
const freshAudit = () => JSON.parse(JSON.stringify(DEMO_SELF_AUDIT));

export function DemoProvider({ children }) {
  const [audit, setAudit] = useState(freshAudit);

  const resetDemo = useCallback(() => setAudit(freshAudit()), []);

  // Local-only: confirm an AI suggestion → adds a user-confirmed observation (no backend).
  const confirmSuggestion = useCallback((pairId, idx, text) => {
    setAudit((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const pair = next.pairs.find((p) => p.pair_id === pairId);
      if (!pair) return prev;
      const obs = pair.ai_draft?.observations?.[idx];
      if (obs) obs.status = "confirmed";
      pair.confirmed_observations.push({ obs_id: `demo_local_${Date.now()}`, source: "ai_confirmed", text: text || obs?.description });
      return next;
    });
  }, []);

  const dismissSuggestion = useCallback((pairId, idx) => {
    setAudit((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const obs = next.pairs.find((p) => p.pair_id === pairId)?.ai_draft?.observations?.[idx];
      if (obs) obs.status = "dismissed";
      return next;
    });
  }, []);

  const editSuggestion = useCallback((pairId, idx, text) => {
    setAudit((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const pair = next.pairs.find((p) => p.pair_id === pairId);
      const obs = pair?.ai_draft?.observations?.[idx];
      if (obs) obs.status = "confirmed";
      pair?.confirmed_observations.push({ obs_id: `demo_local_${Date.now()}`, source: "ai_confirmed", text });
      return next;
    });
  }, []);

  return (
    <DemoContext.Provider value={{ audit, resetDemo, confirmSuggestion, dismissSuggestion, editSuggestion }}>
      {children}
    </DemoContext.Provider>
  );
}

export const useDemo = () => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
};
